/**
 * Which client opened CAP, and where a finished session should be handed back to it.
 *
 * WHY A NAME AND NOT AN ADDRESS. Clients used to tell CAP where to return the session by
 * handing over the address itself, as `redirectTo`. That address is a URL with its own query
 * string, and on the magic-link journey it ends up nested inside CAP's callback URL, inside
 * Supabase's verify URL, inside a tracking link — encoded three times over. It does not
 * reliably survive. A short flat scalar does: in a real sign-in the `org_slug` the auth
 * service writes one line away from `redirectTo` arrived intact while `redirectTo` was gone,
 * and the handoff nonce is already emitted twice, flat and nested, for exactly this reason.
 *
 * So a client names itself, and CAP resolves that name to an address from the table below.
 * Nothing about the address travels.
 *
 * IT ALSO ANSWERS A QUESTION CAP CANNOT OTHERWISE ANSWER. The mobile app opens CAP in an
 * in-app browser, so CAP runs in a browser on both platforms and nothing about its own
 * environment reveals which client it is serving. Guessing is not available — pick the native
 * address and a desktop browser dead-ends, pick the web address and a phone leaves the app
 * mid-sign-in. The client name is the only signal there is, and when it is absent the honest
 * answer is to ask the member, not to guess.
 *
 * Mirrored by lib/clientRegistry.ts in vercel-auth-functions and by the CAP_CLIENT_PARAM /
 * SLEEPIO_*_CLIENT constants in canopy's sleepio-activation feature.
 */
import { CANOPY_POST_AUTH_CALLBACK } from '@/utils/constants';
import { CANOPY_WEB_ORIGIN } from '@/utils/environmentUtils';

/** The query parameter a client names itself in. Short and flat by design. */
export const CLIENT_PARAM = 'client';

/** The Canopy web app — Next.js, in an ordinary browser tab. */
export const SLEEPIO_WEB_CLIENT = 'sleepio-web';

/** The Sleepio mobile app, which opens CAP in an in-app browser. */
export const SLEEPIO_NATIVE_CLIENT = 'sleepio-native';

export const KNOWN_CLIENTS = [SLEEPIO_WEB_CLIENT, SLEEPIO_NATIVE_CLIENT] as const;

export type ClientName = (typeof KNOWN_CLIENTS)[number];

/** The path every client redeems an exchange token on, web and native alike. */
const EXCHANGE_CALLBACK_PATH = '/exchange-callback';

/**
 * The native app's address, fixed rather than configured, and reused rather than rebuilt.
 *
 * A deep link has no environment: the same scheme is registered by every build of the app, so
 * there is nothing per-environment to configure. Only the web origin varies, which is why
 * only the web origin comes from the envSwitch table. Taken from CANOPY_POST_AUTH_CALLBACK so
 * this registry and the existing allowlist cannot disagree about what the app's address is.
 */
const NATIVE_CALLBACK = CANOPY_POST_AUTH_CALLBACK;

export const isKnownClient = (value: unknown): value is ClientName => (
  typeof value === 'string' && (KNOWN_CLIENTS as readonly string[]).includes(value)
);

/**
 * The address for a client name, or undefined when there is none.
 *
 * Undefined has two causes and they are deliberately not distinguished, because the caller
 * does the same thing either way — carries no address and lets the member choose at the end.
 * A name nobody recognises is one; a recognised name whose environment has no deployment is
 * the other, which is the normal state of `sleepio-web` on stage and production today. An
 * environment turns web handoff on by filling in its CANOPY_WEB_ORIGIN entry, and until then
 * it fails closed.
 */
export const addressForClient = (name: unknown): string | undefined => {
  if (!isKnownClient(name)) {
    return undefined;
  }
  if (name === SLEEPIO_NATIVE_CLIENT) {
    return NATIVE_CALLBACK;
  }
  // envSwitch is typed as returning a string but indexes its table with whatever
  // REACT_APP_DEPLOY_ENV holds, so an unset or unrecognised value yields undefined at runtime.
  const origin = (CANOPY_WEB_ORIGIN as string | undefined)?.trim().replace(/\/+$/, '');
  return origin ? `${origin}${EXCHANGE_CALLBACK_PATH}` : undefined;
};

/** Where the client name is kept so it survives a navigation that drops the query string. */
export const CLIENT_SESSION_KEY = 'cap_client';
